import React, { useEffect, useMemo, useState } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';

import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../ui/card';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '../../ui/chart';
import { Heading } from '../../ui/heading';
import { Timestamp } from '../../ui/timestamp';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/table';
import { fetchWithAuth } from '../../../utils/api';

type BillingUsageDays = 7 | 30 | 90;

interface BillingUsageDateRange {
  start_date: string;
  end_date: string;
  days: BillingUsageDays;
}

interface BillingUsageSummary {
  total_requests: number;
  total_provider_requests: number;
  total_tokens: number;
  total_cost_micros: number;
  total_cost_usd: number;
  input_cost_usd: number;
  output_cost_usd: number;
  active_days: number;
  last_request_at: string | null;
}

interface BillingUsageTimeseriesPoint {
  date: string;
  total_cost_micros: number;
  total_cost_usd: number;
  total_requests: number;
  total_provider_requests: number;
  total_tokens: number;
}

interface BillingUsageBreakdownItem {
  key: string;
  label: string;
  total_cost_micros: number;
  total_cost_usd: number;
  total_requests: number;
  total_provider_requests: number;
  total_tokens: number;
}

interface BillingUsageCollectionBreakdownItem {
  collection_id: string | null;
  collection_name: string;
  total_cost_micros: number;
  total_cost_usd: number;
  total_requests: number;
  total_provider_requests: number;
  total_tokens: number;
}

interface BillingUsageAnalyticsResponse {
  date_range: BillingUsageDateRange;
  summary: BillingUsageSummary;
  cost_by_day: BillingUsageTimeseriesPoint[];
  by_provider: BillingUsageBreakdownItem[];
  by_feature: BillingUsageBreakdownItem[];
  by_model: BillingUsageBreakdownItem[];
  by_key_source: BillingUsageBreakdownItem[];
  by_collection: BillingUsageCollectionBreakdownItem[];
}

const DAY_OPTIONS: BillingUsageDays[] = [7, 30, 90];

const EMPTY_USAGE_ANALYTICS: BillingUsageAnalyticsResponse = {
  date_range: {
    start_date: '',
    end_date: '',
    days: 30,
  },
  summary: {
    total_requests: 0,
    total_provider_requests: 0,
    total_tokens: 0,
    total_cost_micros: 0,
    total_cost_usd: 0,
    input_cost_usd: 0,
    output_cost_usd: 0,
    active_days: 0,
    last_request_at: null,
  },
  cost_by_day: [],
  by_provider: [],
  by_feature: [],
  by_model: [],
  by_key_source: [],
  by_collection: [],
};

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 4,
});

const integerFormatter = new Intl.NumberFormat('en-US');

const chartConfig = {
  total_cost_usd: {
    label: 'Daily spend',
    color: 'hsl(var(--primary))',
  },
};

function formatUsd(value: number): string {
  return currencyFormatter.format(value || 0);
}

function formatInteger(value: number): string {
  return integerFormatter.format(value || 0);
}

function formatCompactNumber(value: number): string {
  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value || 0);
}

function formatDateLabel(isoDate: string): string {
  if (!isoDate) {
    return '';
  }
  return new Date(isoDate).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

function BreakdownTable({
  title,
  description,
  rows,
  emptyLabel,
}: {
  title: string;
  description: string;
  rows: BillingUsageBreakdownItem[];
  emptyLabel: string;
}) {
  return (
    <Card className="border-slate-200/80 shadow-sm">
      <CardHeader className="gap-2 border-b border-slate-100">
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        {rows.length === 0 ? (
          <div className="px-6 py-8 text-sm text-muted-foreground">{emptyLabel}</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="pl-6">Name</TableHead>
                <TableHead>Operations</TableHead>
                <TableHead>Provider calls</TableHead>
                <TableHead>Tokens</TableHead>
                <TableHead className="pr-6 text-right">Spend</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.slice(0, 6).map((row) => (
                <TableRow key={`${title}-${row.key}`}>
                  <TableCell className="pl-6 font-medium text-foreground">{row.label}</TableCell>
                  <TableCell>{formatInteger(row.total_requests)}</TableCell>
                  <TableCell>{formatInteger(row.total_provider_requests)}</TableCell>
                  <TableCell>{formatCompactNumber(row.total_tokens)}</TableCell>
                  <TableCell className="pr-6 text-right font-medium text-foreground">
                    {formatUsd(row.total_cost_usd)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

const BillingUsageView: React.FC = () => {
  const [days, setDays] = useState<BillingUsageDays>(30);
  const [usage, setUsage] = useState<BillingUsageAnalyticsResponse>(EMPTY_USAGE_ANALYTICS);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void loadUsage(days, false);
  }, [days]);

  const loadUsage = async (selectedDays: BillingUsageDays, isRefresh: boolean) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError(null);

    try {
      const response = (await fetchWithAuth(
        `/v1/billing/usage?days=${selectedDays}`,
      )) as BillingUsageAnalyticsResponse;
      setUsage(response);
    } catch (fetchError) {
      console.error('Failed to load billing usage analytics', fetchError);
      setError(
        fetchError instanceof Error ? fetchError.message : 'Failed to load billing usage analytics',
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const chartData = useMemo(
    () =>
      usage.cost_by_day.map((point) => ({
        ...point,
        label: formatDateLabel(point.date),
      })),
    [usage.cost_by_day],
  );

  const hasUsage = usage.summary.total_requests > 0;

  const topCollectionRows = useMemo(() => usage.by_collection.slice(0, 6), [usage.by_collection]);

  if (loading) {
    return (
      <div className="h-full px-4 py-5 md:px-6 md:py-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 w-56 rounded bg-slate-200" />
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-4">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="h-32 rounded-3xl bg-slate-100" />
            ))}
          </div>
          <div className="h-80 rounded-3xl bg-slate-100" />
        </div>
      </div>
    );
  }

  return (
    <div className="h-full px-4 py-5 md:px-6 md:py-6">
      <div className="mb-6 flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
        <div>
          <Heading
            level={2}
            className="font-jakarta text-2xl font-extrabold tracking-[-0.02em] text-foreground"
          >
            Usage analytics
          </Heading>
          <p className="mt-2 max-w-3xl text-[13px] text-muted-foreground">
            Canonical LLM usage from <code>llm_usage_logs</code>. This view reflects organization
            spend, logical GO operations, underlying provider API calls, model usage, and
            collection-level billing attribution. Cached prompt tokens are priced at cached-input
            rates when OpenAI returns that detail.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {DAY_OPTIONS.map((option) => (
            <Button
              key={option}
              variant={days === option ? 'default' : 'outline'}
              onClick={() => setDays(option)}
            >
              Last {option} days
            </Button>
          ))}
          <Button
            variant="outline"
            onClick={() => void loadUsage(days, true)}
            disabled={refreshing}
          >
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
        </div>
      </div>

      <div className="mb-6 flex flex-wrap items-center gap-2">
        <Badge variant="outline" className="border-slate-200 bg-slate-50 text-slate-600">
          Window {usage.date_range.start_date || 'N/A'} to {usage.date_range.end_date || 'N/A'}
        </Badge>
        <Badge variant="outline" className="border-slate-200 bg-slate-50 text-slate-600">
          Last request <Timestamp value={usage.summary.last_request_at} format="absolute" fallback="No usage yet" />
        </Badge>
      </div>

      {error && (
        <Card className="mb-6 border-rose-200 bg-rose-50 shadow-none">
          <CardContent className="p-4 text-sm text-rose-700">{error}</CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-4">
        <Card className="border-slate-200/80 shadow-sm">
          <CardHeader className="gap-2">
            <CardDescription>Total spend</CardDescription>
            <CardTitle className="font-jakarta text-3xl tracking-[-0.03em]">
              {formatUsd(usage.summary.total_cost_usd)}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 text-sm text-muted-foreground">
            Input {formatUsd(usage.summary.input_cost_usd)} and output{' '}
            {formatUsd(usage.summary.output_cost_usd)}
          </CardContent>
        </Card>

        <Card className="border-slate-200/80 shadow-sm">
          <CardHeader className="gap-2">
            <CardDescription>Logical operations</CardDescription>
            <CardTitle className="font-jakarta text-3xl tracking-[-0.03em]">
              {formatInteger(usage.summary.total_requests)}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 text-sm text-muted-foreground">
            One row per GO feature operation logged to canonical billing
          </CardContent>
        </Card>

        <Card className="border-slate-200/80 shadow-sm">
          <CardHeader className="gap-2">
            <CardDescription>Provider API calls</CardDescription>
            <CardTitle className="font-jakarta text-3xl tracking-[-0.03em]">
              {formatInteger(usage.summary.total_provider_requests)}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 text-sm text-muted-foreground">
            Underlying OpenAI requests behind those logical operations
          </CardContent>
        </Card>

        <Card className="border-slate-200/80 shadow-sm">
          <CardHeader className="gap-2">
            <CardDescription>Total tokens</CardDescription>
            <CardTitle className="font-jakarta text-3xl tracking-[-0.03em]">
              {formatCompactNumber(usage.summary.total_tokens)}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 text-sm text-muted-foreground">
            Canonical usage across all logged LLM calls
          </CardContent>
        </Card>

        <Card className="border-slate-200/80 shadow-sm">
          <CardHeader className="gap-2">
            <CardDescription>Active days</CardDescription>
            <CardTitle className="font-jakarta text-3xl tracking-[-0.03em]">
              {formatInteger(usage.summary.active_days)}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 text-sm text-muted-foreground">
            Distinct UTC days with canonical usage in the selected window
          </CardContent>
        </Card>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 2xl:grid-cols-[minmax(0,1.4fr)_minmax(360px,0.9fr)]">
        <Card className="border-slate-200/80 shadow-sm">
          <CardHeader className="gap-2 border-b border-slate-100">
            <CardTitle>Daily spend</CardTitle>
            <CardDescription>
              Organization LLM spend over the selected window.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            {!hasUsage ? (
              <div className="flex h-[320px] items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-slate-50 text-sm text-muted-foreground">
                Usage analytics will appear here once summary or classification calls are logged.
              </div>
            ) : (
              <ChartContainer
                config={chartConfig}
                className="h-[320px] rounded-2xl border border-border/70 bg-background/70 p-3"
              >
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border) / 0.7)" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={formatDateLabel}
                      stroke="hsl(var(--muted-foreground))"
                      fontSize={11}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      stroke="hsl(var(--muted-foreground))"
                      fontSize={11}
                      axisLine={false}
                      tickLine={false}
                      width={70}
                      tickFormatter={(value: number) => formatUsd(value)}
                    />
                    <ChartTooltip
                      content={<ChartTooltipContent />}
                      formatter={(value: number) => formatUsd(value)}
                      labelFormatter={(label) => formatDateLabel(label as string)}
                    />
                    <Line
                      type="monotone"
                      dataKey="total_cost_usd"
                      stroke="hsl(var(--primary))"
                      strokeWidth={3}
                      dot={{ fill: 'hsl(var(--primary))', strokeWidth: 0, r: 4 }}
                      activeDot={{ r: 6, fill: 'hsl(var(--primary))' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </ChartContainer>
            )}
          </CardContent>
        </Card>

        <Card className="border-slate-200/80 shadow-sm">
          <CardHeader className="gap-2 border-b border-slate-100">
            <CardTitle>Collection attribution</CardTitle>
            <CardDescription>
              Top collections by canonical spend.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {topCollectionRows.length === 0 ? (
              <div className="px-6 py-8 text-sm text-muted-foreground">
                No collection-attributed usage in this period.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="pl-6">Collection</TableHead>
                    <TableHead>Operations</TableHead>
                    <TableHead>Provider calls</TableHead>
                    <TableHead className="pr-6 text-right">Spend</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {topCollectionRows.map((row) => (
                    <TableRow key={`${row.collection_id ?? 'none'}-${row.collection_name}`}>
                      <TableCell className="pl-6 font-medium text-foreground">
                        {row.collection_name}
                      </TableCell>
                      <TableCell>{formatInteger(row.total_requests)}</TableCell>
                      <TableCell>{formatInteger(row.total_provider_requests)}</TableCell>
                      <TableCell className="pr-6 text-right font-medium text-foreground">
                        {formatUsd(row.total_cost_usd)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-2">
        <BreakdownTable
          title="Providers"
          description="Which LLM provider generated canonical spend."
          rows={usage.by_provider}
          emptyLabel="No provider usage logged in this period."
        />
        <BreakdownTable
          title="Features"
          description="Feature-level attribution from the canonical usage log."
          rows={usage.by_feature}
          emptyLabel="No feature usage logged in this period."
        />
        <BreakdownTable
          title="Models"
          description="Spend and token volume by model snapshot."
          rows={usage.by_model}
          emptyLabel="No model usage logged in this period."
        />
        <BreakdownTable
          title="Key sources"
          description="Whether spend used an organization key or the platform default."
          rows={usage.by_key_source}
          emptyLabel="No provider-key source usage logged in this period."
        />
      </div>
    </div>
  );
};

export default BillingUsageView;
